import { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DndContext, closestCenter, DragMoveEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DataTableSkeleton, Tag, Button, InlineLoading } from '@carbon/react';
import { Renew, Play } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { TableActions } from 'components/table/table-actions';
import {
  DragHeader,
  DragCell,
  handleDragEnd,
  handleDragMove,
  getInitialColumnOrder,
} from 'components/table/draggable';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { scheduledJobsAtom, triggerJob } from './atoms';
import type { ScheduledJob } from './types';
import styles from './page.module.scss';

const columnHelper = createColumnHelper<ScheduledJob>();

// Track which jobs are currently being triggered
type JobStatus = {
  loading: boolean;
  success?: boolean;
  error?: string;
};

type JobStatusMap = Record<string, JobStatus>;

const JobActionsCell: FC<{
  job: ScheduledJob;
  status: JobStatus | undefined;
  onTrigger: (className: string) => void;
}> = ({ job, status, onTrigger }) => {
  if (status?.loading) {
    return (
      <div className={styles.actionCell}>
        <InlineLoading description="Running..." />
      </div>
    );
  }

  return (
    <div className={styles.actionCell}>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Play}
        className={styles.triggerButton}
        onClick={() => onTrigger(job.className)}
      >
        Run Now
      </Button>
      {status?.success && (
        <span className={styles.successMessage}>Triggered!</span>
      )}
      {status?.error && (
        <span className={styles.errorMessage}>{status.error}</span>
      )}
    </div>
  );
};

const createColumns = (
  jobStatuses: JobStatusMap,
  onTrigger: (className: string) => void
) => [
  columnHelper.accessor('className', {
    id: 'className',
    header: 'Job Class',
    size: 320,
    enableSorting: false,
    cell: (info) => <span className={styles.jobName}>{info.getValue()}</span>,
  }),
  columnHelper.accessor('description', {
    id: 'description',
    header: 'Description',
    size: 300,
    enableSorting: false,
    cell: (info) => (
      <span className={styles.jobDescription}>{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor('cronHuman', {
    id: 'cronHuman',
    header: 'Schedule',
    size: 200,
    enableSorting: false,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div>
          <div>{info.getValue()}</div>
          {row.cronExpression && (
            <div className={styles.cronExpression}>{row.cronExpression}</div>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('nextRunAt', {
    id: 'nextRunAt',
    header: 'Next Run',
    size: 180,
    enableSorting: false,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      if (!value) return <span className={styles.jobDescription}>--</span>;
      return (
        <div className={styles.nextRun}>
          <span className={styles.nextRunTime}>
            {new Date(value).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <span className={styles.nextRunRelative}>{row.nextRunRelative}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('lastRunAt', {
    id: 'lastRunAt',
    header: 'Last Run',
    size: 200,
    enableSorting: false,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      if (!value) return <span className={styles.jobDescription}>--</span>;

      const sourceLabel =
        row.lastRunSource === 'manual'
          ? `Manual (${row.lastRunTriggeredBy || 'unknown'})`
          : 'Scheduled';

      return (
        <div className={styles.nextRun}>
          <span className={styles.nextRunTime}>
            {new Date(value).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <span className={styles.nextRunRelative}>{row.lastRunRelative}</span>
          <span className={styles.lastRunSource}>{sourceLabel}</span>
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 150,
    enableSorting: false,
    cell: (info) => (
      <JobActionsCell
        job={info.row.original}
        status={jobStatuses[info.row.original.className]}
        onTrigger={onTrigger}
      />
    ),
  }),
];

const CategoryTable: FC<{
  category: string;
  jobs: ScheduledJob[];
  jobStatuses: JobStatusMap;
  onTrigger: (className: string) => void;
}> = ({ category, jobs, jobStatuses, onTrigger }) => {
  const columns = useMemo(
    () => createColumns(jobStatuses, onTrigger),
    [jobStatuses, onTrigger]
  );

  // Unique table ID per category for persisted column preferences
  const tableId = `scheduled-jobs-${category}-table`;
  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    [columns]
  );

  // Column state hooks
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);
  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);
  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning: { left: [] },
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const table = useReactTable({
    data: jobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    state: {
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    enableColumnPinning: true,
    enableColumnResizing: true,
  });

  const categoryLabels: Record<string, { label: string; tagType: string }> = {
    import: { label: 'Import Jobs', tagType: 'blue' },
    prune: { label: 'Prune Jobs', tagType: 'red' },
    maintenance: { label: 'Maintenance Jobs', tagType: 'purple' },
  };

  const { label, tagType } = categoryLabels[category] || {
    label: category,
    tagType: 'gray',
  };

  return (
    <div className={styles.categorySection}>
      <div className={styles.categoryHeader}>
        <Tag type={tagType as 'blue' | 'red' | 'purple' | 'gray'} size="md">
          {jobs.length}
        </Tag>
        <span className={styles.categoryTitle}>{label}</span>
      </div>
      <div className={styles.stackTableArea}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(e) =>
            handleDragEnd(
              e,
              columnPinning,
              columnOrder,
              setColumnOrder,
              setColumnPinning,
              hoveredColumnIdRef
            )
          }
          modifiers={[restrictToHorizontalAxis]}
          onDragMove={onDragMove}
        >
          <div className={styles.tableContext}>
            <TableActions
              table={table}
              isColumnFilterActive={false}
              setIsColumnFilterActive={() => {}}
              hideColumnFilterButton
            />
          </div>

          <table className={styles.stackTable}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <DragHeader
                        key={header.id}
                        header={header}
                        hoveredColumnIdRef={hoveredColumnIdRef}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <DragCell
                        key={cell.id}
                        cell={cell}
                        hoveredColumnIdRef={hoveredColumnIdRef}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
};

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

export const ScheduledJobsPage: FC = () => {
  const [loadableState, refetch] = useAtom(scheduledJobsAtom);
  const [jobStatuses, setJobStatuses] = useState<JobStatusMap>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Auto-refresh every 30 seconds.
  // NOTE: We might want to eventually flip this to Pusher for real-time updates,
  // but for now since this is an infrequently used back-end admin page, that seemed
  // overly burdensome compared to a simple refresh timeout, which is an accepted pattern.
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [refetch]);

  const handleTrigger = useCallback(async (className: string) => {
    setJobStatuses((prev) => ({
      ...prev,
      [className]: { loading: true },
    }));

    try {
      await triggerJob(className);
      setJobStatuses((prev) => ({
        ...prev,
        [className]: { loading: false, success: true },
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        setJobStatuses((prev) => ({
          ...prev,
          [className]: { loading: false },
        }));
      }, 3000);
    } catch (err) {
      setJobStatuses((prev) => ({
        ...prev,
        [className]: {
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to trigger',
        },
      }));

      // Clear error message after 5 seconds
      setTimeout(() => {
        setJobStatuses((prev) => ({
          ...prev,
          [className]: { loading: false },
        }));
      }, 5000);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  const renderContent = () => {
    if (loadableState.state === 'loading') {
      return <DataTableSkeleton columnCount={6} rowCount={10} />;
    }

    if (loadableState.state === 'hasError') {
      const error = loadableState.error;
      return (
        <div className={styles.error}>
          Error:{' '}
          {error instanceof Error ? error.message : 'Failed to fetch data'}
        </div>
      );
    }

    const data = loadableState.data;
    if (!data || data.jobs.length === 0) {
      return <div className={styles.emptyState}>No scheduled jobs found</div>;
    }

    // Group jobs by category
    const jobsByCategory = data.jobs.reduce<Record<string, ScheduledJob[]>>(
      (acc, job) => {
        const category = job.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(job);
        return acc;
      },
      {}
    );

    // Order categories
    const categoryOrder = ['import', 'prune', 'maintenance'];

    return (
      <>
        <div className={styles.generatedAt}>
          Last updated: {new Date(data.generatedAt).toLocaleString()}
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Renew}
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
        {categoryOrder.map(
          (category) =>
            jobsByCategory[category] && (
              <CategoryTable
                key={category}
                category={category}
                jobs={jobsByCategory[category]}
                jobStatuses={jobStatuses}
                onTrigger={handleTrigger}
              />
            )
        )}
      </>
    );
  };

  return (
    <>
      <PageHeader breadcrumbs={[]} title="Scheduled Jobs" actions={null} />
      <div className={styles.pageContent}>
        <p className={styles.description}>
          View and manually trigger scheduled import and prune jobs. Jobs run on
          the configured cron schedule in Central Time (US & Canada). Use the
          &quot;Run Now&quot; button to trigger a job immediately.
        </p>
        {renderContent()}
      </div>
    </>
  );
};
